import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  IconButton,
} from "@mui/material";
import BaseCustomerTable from "../BaseCustomerTable/BaseCustomerTable";
import "./LoanSetupTable.scss";
import Search from "../../images/search.svg";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import pdfImage from "../../../src/images/pdf-image.svg";
import CSVImage from "../../../src/images/csv-file.png";
import LoanDetailModal from "../LoanTable/LoanDetailModal";
import LoanReportsDetailsModal from "../LoanReportsDetailsModal/LoanReportsDetailsModal";
import Arrow from "../../images/arrow-right-purple.svg";
import EditIcon from "../../images/editpencil.svg";
import ViewDetailsIcon from "../../images/eye.svg";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

// Helper to get nested value using dot notation
const getValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

const LoanSetupTable = ({ data, columns, title = "Loan Report", formatRow, viewDetails, handleViewDetails, isEdit, handleEdit, viewLoanTypes, handleViewLoanTypes, isDelete, handleDelete, viewLoanProducts, handleViewLoanProducts, addLangTag, handleAddLangTag, handleViewLangTags }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [sorting, setSorting] = useState({ column: "", direction: "asc" });
    const [selectedData, setSelectedData] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const visibleColumns = columns.filter(col => !col.hidden);

    console.log("LoanSetupTable_data-----------",data)

    const handleSort = (columnLabel) => {
      const isAsc = sorting.column === columnLabel && sorting.direction === "asc";
      setSorting({ column: columnLabel, direction: isAsc ? "desc" : "asc" });
    };
  
    const sortedData = [...data].sort((a, b) => {
      const col = columns.find((c) => c.label === sorting.column);
      if (!col) return 0;
  
      let aVal = col.getValue(a);
      let bVal = col.getValue(b);
  
      if (!isNaN(aVal) && !isNaN(bVal)) {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (Date.parse(aVal) && Date.parse(bVal)) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
  
      const isAsc = sorting.direction === "asc";
      if (aVal < bVal) return isAsc ? -1 : 1;
      if (aVal > bVal) return isAsc ? 1 : -1;
      return 0;
    });
  
    const handleOpen = (row) => {
      console.log("row------------",row?.li?.LoanID)
      setSelectedData(row?.li?.LoanID);
      setOpen(true);
    };
  
    const handleClose = () => setOpen(false);
  
    const pageCount = Math.ceil(data.length / rowsPerPage);
    const pageNumbers = Array.from({ length: pageCount }, (_, i) => i);

    const handleChangePage = (_, newPage) => {
        if(newPage != pageNumbers?.length) {
          setPage(newPage);
        }
    };
  
    useEffect(() => {
      return () => setSorting({ column: "", direction: "asc" });
    }, []);

    const filteredData = sortedData.filter((row) =>
      columns.some((col) => {
        const val = col.getValue(row);
        return val?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );

    const csvHeaders = columns.map(col => col.label);
    const csvData = [
      [title],
      [],
      csvHeaders,
      ...data.map(row => formatRow ? formatRow(row) : columns.map(col => col.getValue(row)))
    ];

    const runPDF = () => {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(title, 14, 22);
    
      const headers = [csvHeaders];
      const body = data.map(row => formatRow ? formatRow(row) : columns.map(col => col.getValue(row)));
    
      autoTable(doc, {
        head: headers,
        body,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [22, 160, 133] },
      });
    
      doc.save(`${title.replace(/\s+/g, "_")}.pdf`);
    };

    const decodeHtml = (html) => {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    };
  
    return (
      <>
        <Box display="flex" justifyContent="space-between" >
          <Box className="loan-reports-search-container mt-4">
            <TextField
              placeholder="Search Table"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true, 
                sx: {
                  borderRadius: '33px',
                  color: '#000',
                },
                endAdornment: (
                  <IconButton 
                    sx={{ padding: "5px", marginRight: "5px" }}
                  >
                    <img 
                      src={Search} 
                      alt="Search Icon" 
                      style={{ width: "20px", height: "20px" }} 
                      loading="lazy" 
                    />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
              <CSVLink data={csvData} filename={`${title.replace(/\s+/g, "_")}.csv`}>
                <IconButton>
                  <img src={CSVImage} alt="CSV" style={{ width: "40px" }} />
                </IconButton>
              </CSVLink>
              <IconButton onClick={runPDF}>
                <img src={pdfImage} alt="PDF" style={{ width: "30px" }} />
              </IconButton>
            </Box>
        </Box>

        <LoanReportsDetailsModal
          open={open}
          handleClose={handleClose}
          loanid={selectedData}
        />

        <TableContainer component={Paper} sx={{ boxShadow: "none" }} className="loan-setup-table-container">
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col, i) => (
                  <TableCell
                    key={i}
                    align="left"
                    className="transaction-table-headers"
                    onClick={() => handleSort(col.label)}
                  >
                    <div className="header-cell-content">
                      <p className="transaction-table-headers">{col.label}</p>
                      {sorting.column === col.label && (
                        <span className="sort-arrow">
                          {sorting.direction === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </div>
                  </TableCell>
                ))}
                { (viewLoanTypes || viewLoanProducts) && <TableCell className="transaction-table-headers" align="right">
                  <div className="header-cell-content justify-content-end">
                    <p className="transaction-table-headers">{viewLoanTypes ? "View Loan Types" : "View Loan Products"}</p> 
                  </div>
                </TableCell>}
                
                {
                  addLangTag && <TableCell className="transaction-table-headers" align="right">
                    <div className="header-cell-content justify-content-end">
                      <p className="transaction-table-headers">Add Lang Tag</p> 
                    </div>
                  </TableCell>
                }

                <TableCell className="transaction-table-headers" align="right">
                  <div className="header-cell-content justify-content-end">
                    <p className="transaction-table-headers">Action</p>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
  
            <TableBody>
              {
                filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + ((viewLoanTypes || viewLoanProducts) ? 2 : 1)} align="center">
                      <p style={{ padding: "16px", fontSize: "14px", color: "#888" }}>
                        No data available
                      </p>
                    </TableCell>
                  </TableRow>
                ):
                (filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={index}>
                    {visibleColumns.map((col, i) => (
                      <>
                      
                        {
                          handleViewLangTags && col?.label == "Tag" ? <TableCell key={i} align="left" style={col.style || {}}>
                            <p style={{ cursor: "pointer" }} className="transaction-table-date" onClick={()=> handleViewLangTags(row)}>{col.getValue(row)}</p>
                          </TableCell> : 
                          <TableCell key={i} align="left" style={col.style || {}}>
                            {/* <p className="transaction-table-date">{col.getValue(row)}</p> */}
                            <p className="transaction-table-date">
                              {decodeHtml(col.getValue(row))}
                            </p>
                          </TableCell>
                        }
                      </>
                    ))}
                    {
                      (viewLoanTypes || viewLoanProducts) && 
                      <TableCell align="center">
                        <div
                            className="d-flex justify-content-end align-items-center details-container"
                            // onClick={() => handleOpen(row)}
                            style={{ cursor: "pointer" }}
                        >
                            <IconButton
                                aria-label="view details"
                                onClick={()=>viewLoanTypes ? handleViewLoanTypes(row) : viewLoanProducts ? handleViewLoanProducts(row) : null }
                            >
                                <img src={ViewDetailsIcon} alt="View Details" className="edit-icon-crm" loading="lazy" />
                            </IconButton>
                        </div>
                      </TableCell>
                    }

                    {/* Display add lang tag option for LoanLangTagsTable component */}
                    {
                      addLangTag && 
                      <TableCell align="center">
                        <div
                            className="d-flex justify-content-end align-items-center details-container"
                            style={{ cursor: "pointer" }}
                        >
                            <IconButton
                                aria-label="view details"
                                onClick={()=> handleAddLangTag(row) }
                            >
                              <AddIcon sx={{ color: "#778087" }} />
                            </IconButton>
                        </div>
                      </TableCell>
                    }

                    <TableCell align="right">
                        <div
                            className="d-flex justify-content-end align-items-center details-container"
                            // onClick={() => handleOpen(row)}
                            style={{ cursor: "pointer" }}
                        >
                            {isEdit && <IconButton
                                aria-label="edit"
                                onClick={()=>handleEdit(row)}
                            >
                                <img src={EditIcon} alt="Edit" className="edit-icon-crm" loading="lazy" />
                            </IconButton>}

                            {viewDetails && <IconButton
                                aria-label="view details"
                                onClick={()=>handleViewDetails(row)}
                            >
                                <img src={ViewDetailsIcon} alt="View Details" className="edit-icon-crm" loading="lazy" />
                            </IconButton>}

                            {isDelete && <IconButton
                                  aria-label="view details"
                                  onClick={()=>handleDelete(row)}
                              >
                                <DeleteOutlineIcon sx={{ color: "#778087" }} />
                              </IconButton>}
                        </div>
                    </TableCell>
                  </TableRow>
                )))
              }
            </TableBody>
          </Table>
  
          <BaseCustomerTable
            page={page}
            onPageChange={handleChangePage}
            pageNumbers={pageNumbers}
          />
        </TableContainer>
      </>
    );
};

export default LoanSetupTable;